import type {AreaDispatcher} from '@gaia/hooks/useAreas';
import createAsyncMutex from '@gaia/utils/createAsyncMutex';
import debounceWithResolver from '@gaia/utils/debounceWithResolver';
import fetchJSON from '@gaia/utils/fetchJSON';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

import getAreaPolygon from './getAreaPolygon';

const debouncedFetch = debounceWithResolver(window.fetch, 500);

export const savedAreas: Record<string, Area> = {};

export function getIsAreaModifiedSinceSave(area: Area) {
    const savedArea = savedAreas[area.properties.localId];
    const omitProps = [
        'properties.id',
        'properties.localId',
        'properties.isLocallyCreated',
        'properties.isLatestImport',
    ];

    if (!savedArea && area.properties.deleted) return false;
    else if (!savedArea) return true;
    else if (isEqual(omit(savedArea, omitProps), omit(area, omitProps))) return false;
    else if (savedArea.properties.deleted) return false;
    else return true;
}

export function registerSavedArea(area: Area) {
    savedAreas[area.properties.localId] = area;
}

export default createAsyncMutex(
    async (area: Area, areaDispatcher: AreaDispatcher): Promise<void> => {
        const isModifiedSinceSave = getIsAreaModifiedSinceSave(area);
        if (!isModifiedSinceSave) return;
        registerSavedArea(area);

        const body = JSON.stringify({
            type: 'Feature',
            properties: {
                ...omit(area.properties, ['localId', 'id', 'type']),
                hexcolor: area.properties.color,
            },
            geometry: getAreaPolygon(area),
        });

        if (area.properties.id) {
            await debouncedFetch(`/api/objects/area/${area.properties.id}/`, {
                method: 'PUT',
                credentials: 'same-origin',
                headers: {'Content-Type': 'application/json'},
                body,
            });
        } else {
            const response = await fetchJSON('/api/objects/area/', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {'Content-Type': 'application/json'},
                body,
            });
            const id = response.features[0].properties.id;
            areaDispatcher({
                type: 'updateProperties',
                properties: {
                    id,
                    localId: area.properties.localId,
                },
            });
        }
    },
    (area) => area.properties.localId
);
